import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, SceneHead, routesFor, type Lang } from "../primitives";

const t = {
  es: {
    aria: "Diagnóstico y contacto",
    label: "Diagnóstico y contacto",
    title: "Identifica qué está frenando las oportunidades de tu empresa.",
    intro:
      "Reunión inicial gratuita de 30 minutos para conocer las necesidades de tu empresa, revisar el contexto general y determinar posibles siguientes pasos.",
    note:
      "No incluye auditoría SEO, diagnóstico técnico, reporte, investigación de palabras clave, análisis competitivo ni implementación. La sesión se agenda según disponibilidad; no es inmediata.",
    cta1: "Solicitar reunión inicial",
    cta2: "Consultoría de pago",
    formNote:
      "El formulario, sus validaciones y su política de privacidad son los de la página de diagnóstico; no se duplica el envío ni se simulan respuestas.",
    steps: [
      {
        n: "01",
        t: "Solicitud",
        d: "Envías el formulario con tu sitio, tu mercado y lo que quieres resolver.",
      },
      {
        n: "02",
        t: "Contexto general",
        d: "Se revisa la información que compartes para preparar una conversación útil sobre tu situación actual.",
      },
      {
        n: "03",
        t: "Coordinación de la sesión",
        d: "Se acuerda una fecha para la reunión de 30 minutos. La disponibilidad se confirma al responder.",
      },
    ],
  },
  en: {
    aria: "Diagnosis and contact",
    label: "Diagnosis and contact",
    title: "Find out what is holding back your company's opportunities.",
    intro:
      "Free 30-minute initial consultation to understand your company’s needs, review the general context and determine possible next steps.",
    note:
      "It does not include an SEO audit, technical diagnosis, report, keyword research, competitor analysis or implementation. The session is scheduled based on availability; it is not immediate.",
    cta1: "Request initial consultation",
    cta2: "Paid consulting",
    formNote:
      "The form, its validations and its privacy policy are the ones on the diagnosis page; submissions are not duplicated and responses are not simulated.",
    steps: [
      {
        n: "01",
        t: "Request",
        d: "You send the form with your site, your market and what you want to solve.",
      },
      {
        n: "02",
        t: "General context",
        d: "The information you share is reviewed to prepare a useful conversation about your current situation.",
      },
      {
        n: "03",
        t: "Session scheduling",
        d: "A date is agreed for the 30-minute consultation. Availability is confirmed in the reply.",
      },
    ],
  },
} as const;

export const SceneDiagnosis = ({ language = "es" as Lang }: { language?: Lang }) => {
  const c = t[language];
  const routes = routesFor(language);

  return (
    <section
      className="v3-section bg-[color:var(--v3-mineral)]"
      aria-label={c.aria}
      id="diagnostico"
    >
      <div className="v3-shell grid gap-12 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-6">
          <SceneHead index="09" label={c.label} title={c.title} intro={c.intro} />

          <p className="v3-note mt-6">{c.note}</p>

          <div className="mt-9 flex flex-wrap items-center gap-7">
            <Link to={routes.seoDiagnosis} className="v3-btn">
              {c.cta1}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
            <Link to={routes.booking} className="v3-btn-ghost">
              {c.cta2}
            </Link>
          </div>
          <p className="mt-4 text-[0.85rem] text-[color:var(--v3-muted)]">{c.formNote}</p>
        </div>

        <div className="lg:col-span-6">
          <ol className="bg-white p-8 lg:p-10">
            {c.steps.map((s, i) => (
              <Reveal as="li" key={s.n} delay={0.05 * i}>
                <div
                  className={`flex gap-6 ${
                    i > 0 ? "mt-8 border-t border-[color:var(--v3-line)] pt-8" : ""
                  }`}
                >
                  <span className="v3-micro pt-1" style={{ color: "var(--v3-blue)" }}>
                    {s.n}
                  </span>
                  <div>
                    <h3 className="text-[1.05rem] font-semibold">{s.t}</h3>
                    <p className="v3-body mt-2 text-[0.97rem]">{s.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};
